import {Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {InternalPropertyMap} from "../../../basic-entity-back/basic-entity-interface/mapping-internal";
import {AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup} from "@angular/forms";
import {BasicEntityInputComponent} from "../../basic-entity-input/basic-entity-input.component";
import {TypeStr} from "../../../basic-entity-back/property-type/type-str";
import {BaseDialog} from "../../basic-entity-table/base-dialog";
import {Resource} from "../../../api/resource";
import {BaseDialogData, BaseDialogResult} from "../../basic-entity-table/base-dialog-data";
import {EditionDialogComponent} from "../edition-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {InterfaceProviderService} from "../../../basic-entity-back/services/interface-provider.service";
import {Moment} from "moment";
import {Subscription} from "rxjs";
import {startWith} from "rxjs/operators";
import {NestedBehavior} from "../../../basic-entity-back/property-type/nested-model-type";

export interface FormManager {
    createControl(property: InternalPropertyMap, val: any, index: number | null);
}

@Component({
    selector: 'be-edition-row',
    templateUrl: './edition-row.component.html',
    styleUrls: ['./edition-row.component.scss']
})
export class EditionRowComponent implements OnInit, OnDestroy {
    public readonly UNIQUE_CONTROL_NAME = BasicEntityInputComponent.UNIQUE_CONTROL_NAME;

    /** The property managed in this row */
    @Input() public property: InternalPropertyMap;
    /** The formGroup complete */
    @Input() public formGroup: UntypedFormGroup;
    /** The form manager */
    @Input() protected readonly formManager: FormManager;
    /** Date picker filter for the date picker to set (if it corresponds to the column) */
    @Input() public datePickerFilter: (date: Moment | null) => boolean = undefined;
    /** (Optional) Function to process every created model when this is an array and we click "add",
     * before sending it to the edition dialog */
    @Input() public modelInitialisation: (m: Resource) => Resource = m => m;
    @Input() public callbackFunctionForDeleteButton: (...args: any) => void;
    @Output() public deleteButton = new EventEmitter<UntypedFormArray>();

    @HostBinding('class.be-edition-row') public readonly classValue = true;
    @HostBinding('style.visibility') public visibility = 'visible';

    private _dependsOnSubscription: Subscription | null;

    constructor(private _fb: UntypedFormBuilder,
                private _matDialog: MatDialog,
                private _interfaceProvider: InterfaceProviderService) {
    }

    /**
     * If a column is an array, the template will call this function to add
     * a new element to the array when pressing the adding button
     * @param property - The column in which the add button has been pressed
     */
    public addClick(property: InternalPropertyMap) {
        if (property.type.toString() === TypeStr.NestedModel) {
            const nestedType = property.type.asNestedModel();
            if (nestedType.behavior === NestedBehavior.CreateOnly) {
                const interf = this._interfaceProvider.interfaceForModel(nestedType.modelType);
                const newModel = this.modelInitialisation(interf.serialiser.getEmptyModel());
                this._matDialog.open<BaseDialog<Resource>, BaseDialogData<Resource>, BaseDialogResult>(
                    nestedType.creationDialog || EditionDialogComponent,
                    {
                        disableClose: true,
                        autoFocus: true,
                        restoreFocus: true,
                        data: nestedType.baseDialogDataForModel(interf, newModel, true)
                    }
                ).afterClosed().subscribe(result => {
                    if (result === BaseDialog.RESULT_SHOULD_RELOAD) {
                        this.addArrayElement(property, newModel);
                    }
                });
                return;
            }
        }
        this.addArrayElement(property, "");
    }

    ngOnInit(): void {
        if (this.property.dependsOn != null) {
            const control = this.formGroup.get(this.property.dependsOn);
            if (control) {
                this._dependsOnSubscription = control.valueChanges
                    .pipe(startWith([control.value]))
                    .subscribe(visible => {
                        this.formGroup.get(this.property.modelKey).updateValueAndValidity();
                        this.visibility = visible ? 'visible' : 'hidden';
                    });
            }
        }
    }

    ngOnDestroy(): void {
        if (this._dependsOnSubscription) {
            this._dependsOnSubscription.unsubscribe();
        }
    }

    /**
     * Add an element to the array of an array property (provided the value)
     * @param property
     * @param val
     */
    public addArrayElement(property: InternalPropertyMap, val: any) {
        const formArray = this.formGroup.get(property.modelKey) as UntypedFormArray;
        formArray.push(this.formManager.createControl(property, val, formArray.length));
    }

    /**
     * If the column is an array, the template will call this function to
     * delete elements from the array, when pressing the delete button.
     * @param property - The property in which the delete button has been pressed
     * @param index - The index of the row to delete
     */
    public deleteArrayElement(property: InternalPropertyMap, index: number) {
        if (this.callbackFunctionForDeleteButton) {
            (this.formGroup.get(property.modelKey) as UntypedFormArray).at(index).disable();
            this.callbackFunctionForDeleteButton(index);
            this.deleteButton.emit((this.formGroup.get(property.modelKey) as UntypedFormArray));
        } else {
            (this.formGroup.get(property.modelKey) as UntypedFormArray).removeAt(index);
        }
    }

    public deleteDisabled(property: InternalPropertyMap, index: number): boolean {
        return (this.formGroup.get(property.modelKey) as UntypedFormArray).at(index).disabled;

    }

    public getControls() {
      return (this.formGroup.controls[this.property.modelKey] as UntypedFormArray).controls
    }

    public getControlAsFormGroup(control: AbstractControl) {
      return control as UntypedFormGroup;
    }
}
